import { z } from "zod";
import { MARKETPLACE_OPERATOR_LEVEL_VALUES } from "./operator-level.shared";

const marketplaceSourceContextKindSchema = z.enum([
  "personal",
  "organization",
  "team",
]);

export const marketplaceSourceContextSchema = z
  .object({
    kind: marketplaceSourceContextKindSchema,
    orgId: z.string().optional(),
    orgName: z.string().optional(),
    teamId: z.string().optional(),
    teamName: z.string().optional(),
    teamKey: z.string().optional(),
    label: z.string(),
  })
  .strip();

const marketplaceIntegrationSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional().default(""),
  required: z.boolean().default(false),
  configurationNotes: z.string().optional().default(""),
});

/** URL slug: normalized to lowercase; must be kebab-safe (letters, numbers, hyphens). */
const marketplaceListingSlugSchema = z
  .string()
  .min(3, "Slug needs at least 3 characters")
  .max(120, "Slug is too long (max 120 characters)")
  .transform((s) => s.trim().toLowerCase())
  .refine((s) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s), {
    message:
      "Slug can only use lowercase letters, numbers, and hyphens (e.g. my-listing).",
  });

/** Step 1 — source + listing basics (validate on Next). */
export const marketplaceDraftStep1Schema = z.object({
  slug: marketplaceListingSlugSchema,
  title: z
    .string()
    .min(3, "Enter a title (at least 3 characters)")
    .max(120, "Title is too long (max 120 characters)"),
  shortDescription: z
    .string()
    .min(10, "Add a short description (at least 10 characters)")
    .max(220, "Short description is too long (max 220 characters)"),
  categoryId: z.string().min(1, "Select a category"),
  subCategoryId: z.string().optional(),
  tags: z.array(z.string().min(1)).max(12).default([]),
  sourceWorkflowId: z
    .string()
    .min(1, "Select a source operator")
    .refine((v) => v !== "pending-selection", "Select a source operator"),
  sourceWorkflowVersionId: z
    .string()
    .min(1, "Select a source version")
    .refine((v) => v !== "pending-selection", "Select a source version"),
});

/** Step 2 — content, media context, support (validate on Next). */
export const marketplaceDraftStep2Schema = z.object({
  summary: z
    .string()
    .min(20, "Add a summary (at least 20 characters)")
    .max(500, "Summary is too long (max 500 characters)"),
  description: z
    .string()
    .min(40, "Add a description (at least 40 characters)")
    .max(10_000, "Description is too long (max 10,000 characters)"),
  useCases: z.array(z.string().min(1)).min(1, "Add at least one use case"),
  setupInstructions: z
    .array(z.string().min(1))
    .min(1, "Add at least one setup instruction"),
  supportEmail: z
    .string()
    .default("")
    .transform((s) => s.trim())
    .refine((s) => s === "" || z.string().email().safeParse(s).success, {
      message: "Enter a valid support email",
    }),
});

const marketplaceEnvVarSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  required: z.boolean().default(false),
  isSecret: z.boolean().default(false),
  exampleValue: z.string().optional(),
});

const marketplacePricingModelSchema = z.enum(["free", "paid", "contact_sales"]);

const marketplaceBillingCycleSchema = z.enum(["one_time", "monthly", "yearly"]);

const marketplaceLicenseTypeSchema = z.enum(["standard", "custom", "internal"]);

const marketplaceDifficultySchema = z.enum(["easy", "medium", "advanced"]);

const marketplaceOperatorLevelSchema = z.enum(
  MARKETPLACE_OPERATOR_LEVEL_VALUES,
);

/** Max sub-workflows stored on a listing version (Employee Pack). */
export const EMPLOYEE_PACK_MEMBER_MAX = 50;

const employeePackMemberSchema = z.object({
  workflowPublicId: z.string().min(1),
  versionPublicId: z.string().min(1).optional(),
  displayName: z.string().optional(),
  description: z.string().optional(),
  referencingStepIds: z.array(z.string()).optional(),
  source: z.enum(["extracted", "manual"]).default("extracted"),
  marketplaceSlug: z.string().optional(),
  marketplaceIconAssetId: z.string().optional().nullable(),
  lastResolvedAt: z.string().optional(),
});

export type EmployeePackMember = z.infer<typeof employeePackMemberSchema>;

export const marketplaceDraftSchema = z
  .object({
    slug: marketplaceListingSlugSchema,
    title: z
      .string()
      .min(3, "Enter a title (at least 3 characters)")
      .max(120, "Title is too long (max 120 characters)"),
    shortDescription: z
      .string()
      .min(10, "Add a short description (at least 10 characters)")
      .max(220, "Short description is too long (max 220 characters)"),
    summary: z
      .string()
      .min(20, "Add a summary (at least 20 characters)")
      .max(500, "Summary is too long (max 500 characters)"),
    description: z
      .string()
      .min(40, "Add a description (at least 40 characters)")
      .max(10_000, "Description is too long (max 10,000 characters)"),
    categoryId: z.string().min(1).optional(),
    subCategoryId: z.string().optional(),
    tags: z.array(z.string().min(1)).max(12).default([]),
    supportEmail: z
      .string()
      .default("")
      .transform((s) => s.trim())
      .refine((s) => s === "" || z.string().email().safeParse(s).success, {
        message: "Enter a valid support email",
      }),
    videoUrl: z.string().url().optional().or(z.literal("")),
    authorDisplayName: z.string().max(120).optional().or(z.literal("")),
    authorBio: z.string().max(3000).optional(),
    organizationDisplayName: z.string().max(160).optional(),
    requiredIntegrations: z.array(marketplaceIntegrationSchema).default([]),
    optionalIntegrations: z.array(marketplaceIntegrationSchema).default([]),
    requiredEnvVars: z.array(marketplaceEnvVarSchema).default([]),
    optionalEnvVars: z.array(marketplaceEnvVarSchema).default([]),
    useCases: z.array(z.string().min(1)).max(12).default([]),
    prerequisites: z.array(z.string().min(1)).max(20).default([]),
    setupInstructions: z.array(z.string().min(1)).max(30).default([]),
    postOnboardingChecklist: z.array(z.string().min(1)).max(20).default([]),
    limitations: z.array(z.string().min(1)).max(20).default([]),
    knownIssues: z.array(z.string().min(1)).max(20).default([]),
    complianceNotes: z.array(z.string().min(1)).max(20).default([]),
    sampleInput: z.unknown().optional(),
    sampleOutput: z.unknown().optional(),
    pricingModel: marketplacePricingModelSchema.default("free"),
    billingCycle: marketplaceBillingCycleSchema.default("one_time"),
    price: z.number().int().nullable().optional(),
    currency: z.string().optional(),
    licenseType: marketplaceLicenseTypeSchema.default("standard"),
    estimatedSetupTimeMinutes: z.number().int().positive().optional(),
    estimatedRunTimeSeconds: z.number().int().positive().optional(),
    estimatedDifficulty: marketplaceDifficultySchema.optional(),
    targetOperatorLevel: z.preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      marketplaceOperatorLevelSchema.optional(),
    ),
    isEnterpriseReady: z.boolean().default(false),
    isFeaturedCandidate: z.boolean().default(false),
    supportedRegions: z.array(z.string()).default([]),
    supportedLanguages: z.array(z.string()).default([]),
    sourceWorkflowId: z.string().min(1),
    sourceWorkflowVersionId: z.string().min(1),
    iconAssetId: z.string().optional(),
    coverAssetId: z.string().optional(),
    galleryAssetIds: z.array(z.string()).max(10).default([]),
    employeePackMembers: z
      .array(employeePackMemberSchema)
      .max(EMPLOYEE_PACK_MEMBER_MAX)
      .default([]),
  })
  .superRefine((data, ctx) => {
    // Paid listings must have a price of at least 1 cent — $0 is effectively free.
    if (data.pricingModel === "paid") {
      if (data.price == null || data.price < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["price"],
          message:
            "Paid listings must have a price of at least $0.01. Use 'Free' pricing model for $0 listings.",
        });
      }
    }
  });

export type MarketplaceSourceContext = z.infer<
  typeof marketplaceSourceContextSchema
>;
export type MarketplaceDraftInput = z.infer<typeof marketplaceDraftSchema>;
